import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { CheckCircle2, Clock, TrendingUp } from "lucide-react";

const TIERS = [
  { higher:     200, match:    100, bonus:    1500 },
  { higher:     600, match:    300, bonus:    4500 },
  { higher:    1200, match:    600, bonus:    9000 },
  { higher:    2400, match:   1200, bonus:   18000 },
  { higher:    4800, match:   2400, bonus:   36000 },
  { higher:   12000, match:   6000, bonus:   90000 },
  { higher:   24000, match:  12000, bonus:  180000 },
  { higher:   48000, match:  24000, bonus:  360000 },
  { higher:   96000, match:  48000, bonus:  720000 },
  { higher:  192000, match:  96000, bonus: 1440000 },
  { higher:  384000, match: 192000, bonus: 2880000 },
  { higher:  768000, match: 384000, bonus: 4000000 },
  { higher: 1536000, match: 768000, bonus: 6000000 },
];

export default function DistMonthlyBonus() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dist/monthly-bonus").then((r) => setD(r.data)).catch(() => setD({ paid_tiers: [], current_higher: 0, current_match: 0, last_month_paid: 0 }));
  }, []);

  return (
    <DashboardLayout title="Monthly Bonus (2:1)" subtitle="Track your Monthly Bonus levels and qualification">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <StatCard label="Higher-side SP" value={d?.current_higher ?? "—"} tone="royal" />
        <StatCard label="Matching-side SP" value={d?.current_match ?? "—"} tone="gold" />
        <StatCard label="Last month bonus" value={inr(d?.last_month_paid ?? 0)} tone="olive" />
      </div>

      <div className="bg-white border border-line rounded-sm">
        <div className="px-6 py-4 border-b border-line flex items-center gap-2">
          <TrendingUp size={16} className="text-gold-deep" />
          <div className="font-serif text-lg text-royal">Bonus levels (2:1 ratio · first-time claim · 13 levels)</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-ivory text-navy/60 border-b border-line">
                <th className="text-left px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Level</th>
                <th className="text-right px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Higher-side SP</th>
                <th className="text-right px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Matching-side SP</th>
                <th className="text-right px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Bonus</th>
                <th className="text-center px-6 py-3 font-medium text-[11px] uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.map((t, i) => {
                const paid = (d?.paid_tiers || []).includes(i + 1);
                return (
                  <tr key={i} data-testid={`mbonus-level-${i + 1}`} className="border-b border-line last:border-0 hover:bg-ivory/50">
                    <td className="px-6 py-3 font-serif text-royal">Level {i + 1}</td>
                    <td className="px-6 py-3 text-right tabular-nums lining-nums">{t.higher.toLocaleString("en-IN")} SP</td>
                    <td className="px-6 py-3 text-right tabular-nums lining-nums">{t.match.toLocaleString("en-IN")} SP</td>
                    <td className="px-6 py-3 text-right tabular-nums lining-nums font-medium text-gold-deep">{inr(t.bonus)}</td>
                    <td className="px-6 py-3 text-center">
                      {paid ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-olive/10 text-olive border border-olive/20 rounded-sm text-[10px] uppercase tracking-widest"><CheckCircle2 size={10} /> Paid</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy/[0.04] text-navy/50 border border-line rounded-sm text-[10px] uppercase tracking-widest"><Clock size={10} /> Pending</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-line bg-ivory/60 text-[11px] text-navy/60 italic">
          Monthly Bonus is <strong>2:1</strong> and paid on <strong>first-time</strong> achievement of each level. Weekly matching remains 1:1 — the two are separate mechanisms.
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatCard({ label, value, tone }) {
  const toneMap = {
    royal: "border-royal/20 bg-royal/[0.04] text-royal",
    gold:  "border-gold/30 bg-gold/[0.08] text-gold-deep",
    olive: "border-olive/30 bg-olive/[0.06] text-olive",
  };
  return (
    <div data-testid={`monthly-bonus-${label.toLowerCase().replace(/ /g,'-')}`} className={`p-5 border-2 rounded-sm ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">{label}</div>
      <div className="text-3xl mt-2 font-extrabold tabular-nums lining-nums">{value}</div>
    </div>
  );
}
